import React from "react";
import styled from "styled-components";
import { Link } from "react-router-dom";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";

const PageContainer = styled.div`
  padding-top: 80px;
  min-height: 100vh;
  background: linear-gradient(135deg, #f0faf4 0%, #e6f7ed 100%);
`;

const ContentWrapper = styled.div`
  max-width: 900px;
  margin: 0 auto;
  padding: 40px 20px;
`;

const Breadcrumbs = styled.nav`
  margin-bottom: 30px;
  font-size: 14px;
  color: #666;

  a {
    color: #1a9d4a;
    text-decoration: none;
    &:hover {
      text-decoration: underline;
    }
  }

  span {
    margin: 0 8px;
    color: #999;
  }
`;

const Header = styled.header`
  text-align: center;
  margin-bottom: 40px;
`;

const AppBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  background: #2bee79;
  color: #0a3d1f;
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 20px;
`;

const Title = styled.h1`
  font-size: 2.5rem;
  color: #1a1a1a;
  margin-bottom: 10px;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const Subtitle = styled.p`
  color: #666;
  font-size: 16px;
  max-width: 600px;
  margin: 0 auto;
`;

const Card = styled.div`
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  padding: 40px;
  margin-bottom: 30px;

  @media (max-width: 768px) {
    padding: 25px;
  }
`;

const Section = styled.section`
  margin-bottom: 35px;
  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionTitle = styled.h2`
  color: #1a1a1a;
  font-size: 1.4rem;
  margin-bottom: 15px;
  padding-bottom: 10px;
  border-bottom: 2px solid #e6f7ed;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const Paragraph = styled.p`
  color: #444;
  line-height: 1.8;
  margin-bottom: 15px;
  font-size: 15px;
`;

const List = styled.ul`
  color: #444;
  line-height: 1.8;
  margin-bottom: 15px;
  padding-left: 25px;

  li {
    margin-bottom: 10px;
    font-size: 15px;
  }
`;

const ContactCard = styled.div`
  background: linear-gradient(135deg, #1a9d4a 0%, #127a38 100%);
  color: white;
  padding: 30px;
  border-radius: 12px;
  text-align: center;

  h3 {
    margin-bottom: 15px;
    font-size: 1.3rem;
  }

  p {
    margin-bottom: 10px;
    font-size: 15px;
  }

  a {
    color: white;
    font-weight: 600;
  }
`;

const FAQItem = styled.div`
  margin-bottom: 20px;
  padding: 20px;
  background: #f5fdf8;
  border-radius: 10px;
  border-left: 4px solid #2bee79;

  h4 {
    color: #1a1a1a;
    font-size: 1rem;
    margin-bottom: 8px;
  }

  p {
    color: #555;
    font-size: 14px;
    line-height: 1.7;
    margin: 0;
  }
`;

const RelatedLinks = styled.div`
  display: flex;
  gap: 15px;
  margin-top: 30px;
  flex-wrap: wrap;

  a {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    background: #e6f7ed;
    color: #1a9d4a;
    padding: 12px 20px;
    border-radius: 8px;
    text-decoration: none;
    font-weight: 500;
    transition: all 0.2s;

    &:hover {
      background: #1a9d4a;
      color: white;
    }
  }
`;

export const TipShiftSupportPage = () => {
  return (
    <>
      <Navbar />
      <PageContainer>
        <ContentWrapper>
          <Breadcrumbs>
            <Link to="/">Home</Link>
            <span>&rsaquo;</span>
            <span>Support - TipShift</span>
          </Breadcrumbs>

          <Header>
            <AppBadge>TipShift</AppBadge>
            <Title>Support Center</Title>
            <Subtitle>
              We're here to help. Find answers to common questions or contact us
              directly.
            </Subtitle>
          </Header>

          <Card>
            <Section>
              <SectionTitle>Frequently Asked Questions</SectionTitle>

              <FAQItem>
                <h4>How do I track my tips?</h4>
                <p>
                  Open TipShift and tap the main entry screen. Enter your tip
                  amount, select the receipt method (cash, credit card, or
                  other), and optionally log your hours worked. Your tip is saved
                  instantly and available in your history.
                </p>
              </FAQItem>

              <FAQItem>
                <h4>How does the hourly rate work?</h4>
                <p>
                  When you log both your tip amount and hours worked, TipShift
                  automatically calculates your effective hourly rate. This helps
                  you understand your true earnings per hour, which is useful for
                  comparing shifts and tracking trends over time.
                </p>
              </FAQItem>

              <FAQItem>
                <h4>Is my data secure?</h4>
                <p>
                  Yes. TipShift uses an offline-first architecture, so your data
                  is stored locally on your device and synced securely to our
                  servers via HTTPS/TLS encryption. Authentication tokens are
                  stored in your device's secure keychain. See our{" "}
                  <Link to="/tipshift/privacy">Privacy Policy</Link> for full
                  details.
                </p>
              </FAQItem>

              <FAQItem>
                <h4>How do I cancel my subscription?</h4>
                <p>
                  TipShift Pro subscriptions are managed through Apple or Google.
                  On iOS, go to Settings &gt; Apple ID &gt; Subscriptions and
                  select TipShift. On Android, open the Google Play Store &gt;
                  Subscriptions. You can cancel at any time and retain access
                  until the end of your billing period.
                </p>
              </FAQItem>

              <FAQItem>
                <h4>How do I export my data?</h4>
                <p>
                  With TipShift Pro, you can export your tip history as CSV or
                  PDF. This is especially useful at tax time for generating
                  records of your reported tips. Go to your history and use the
                  export option.
                </p>
              </FAQItem>

              <FAQItem>
                <h4>How do I delete my account?</h4>
                <p>
                  You can delete your account and all associated data from
                  Settings &gt; Delete Account within the app. This action is
                  permanent and cannot be undone. You can also request account
                  deletion by emailing us.
                </p>
              </FAQItem>
            </Section>

            <Section>
              <SectionTitle>Contact Support</SectionTitle>
              <Paragraph>
                If you didn't find the answer you were looking for, our team is
                available to help:
              </Paragraph>
              <ContactCard>
                <h3>Monaco IT Services</h3>
                <p>
                  Email:{" "}
                  <a href="mailto:support@tipshift.app">support@tipshift.app</a>
                </p>
                <p>
                  Website:{" "}
                  <a
                    href="https://monaco-it-services.cl"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    monaco-it-services.cl
                  </a>
                </p>
                <p>Hours: Monday to Friday, 9:00 AM - 6:00 PM (ET)</p>
              </ContactCard>
            </Section>

            <Section>
              <SectionTitle>App Information</SectionTitle>
              <List>
                <li>
                  <strong>Name:</strong> TipShift
                </li>
                <li>
                  <strong>Developer:</strong> Monaco IT Services
                </li>
                <li>
                  <strong>Platforms:</strong> iOS and Android
                </li>
                <li>
                  <strong>Version:</strong> 1.0
                </li>
              </List>
            </Section>

            <RelatedLinks>
              <Link to="/tipshift/privacy">Privacy Policy</Link>
              <Link to="/">Back to Home</Link>
            </RelatedLinks>
          </Card>
        </ContentWrapper>
      </PageContainer>
      <Footer />
    </>
  );
};
